import { DATE_SUBMIT_YMD, DATE_TIME_SUBMIT_ISO_JST, DISPPLAY_MOMENT_YMD, DISPPLAY_MOMENT_YMD_HM } from '@/configs/constants';
import { OrderTime } from '@/types/order';
import { TFunction } from 'i18next';
import moment from 'moment';

export const calculateAge = (birthday: string) => {
    const today = new Date();
    const [birthYear, birthMonth] = birthday.split('-').map(Number);

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    let years = currentYear - birthYear;
    let months = currentMonth - birthMonth;

    if (months < 0) {
        years--;
        months += 12;
    }
    if (months === 0) {
        months = 1;
    }
    return {
        years: years > 0 ? years : 0,
        months: months
    };
};

interface formatDateTimeOptions {
    returnDefault?: null | '',
    onlyTime?: boolean,
    currentformat?: string,
    useLocalTime: boolean,
    useServerTime?: boolean
}

export const formatDateTime = (
    datetime?: string | Date | null,
    format?: string,
    options?: formatDateTimeOptions
) => {
    if (!datetime) return options?.returnDefault ?? null;

    const useLocalTime = options?.useLocalTime ?? true;
    const addJstOffset = (m: moment.Moment) => m.clone().add(options?.useServerTime ? 0 : 9, 'hours'); // UTC + 9h

    // Trường hợp chỉ định định dạng đầu vào (currentformat)
    if (options?.onlyTime && options?.currentformat) {
        const parsed = moment(datetime, [moment.ISO_8601, options.currentformat]);
        return useLocalTime ?
            parsed.format(format) :
            addJstOffset(parsed.utc()).format(format);
    }

    // Trường hợp datetime là Date object
    if (datetime instanceof Date) {
        const parsed = moment(datetime);
        return useLocalTime ?
            parsed.format(format) :
            addJstOffset(parsed.utc()).format(format);
    }

    // Trường hợp datetime là string hoặc number
    const parsed = moment(datetime);

    return useLocalTime ?
        parsed.format(format) :
        addJstOffset(parsed.utc()).format(format);
};

export const formatTime = (input: string | null | undefined): string => {
    if (!input) {
        return '--';
    }
    const [hours, minutes] = input.split(':');
    return `${hours}:${minutes}`;
};

export const formatTotalTime = (input: OrderTime, t: TFunction) => {
    let totalTime = '';
    const { M = 0, W = 0, D = 0, H = 0 } = input;
    if (H === 0 && W === 0 && D === 0 && M === 0) {
        totalTime = t('time.zero_hour');
    } else {
        if (H > 0) {
            totalTime = H === 1 ? t('time.one_hour') : t('time.hours', { hour: H });
        } else {
            (M > 0) && (totalTime += M === 1 ? t('time.one_month') : t('time.months', { month: M }));
            (W > 0) && (totalTime += W === 1 ? ` ${t('time.one_week')} ` : ` ${t('time.weeks', { week: W })} `);
            (D > 0) && (totalTime += D === 1 ? ` ${t('time.one_day')} ` : ` ${t('time.days', { day: D })} `);
        }
    }

    return totalTime;
};

export const isValidYYYYMMDDHHmmss = (datetime: string): boolean => {
    const format = 'YYYYMMDDHHmmss';
    return moment(datetime, format, true).isValid(); // strict mode
};

export const formatDisplayDateTime = (date?: Date, time?: string | null, dateFormat: string = DISPPLAY_MOMENT_YMD_HM) => {
    if (!date) return '';

    // Kiểm tra nếu `time` rỗng, sử dụng '00:00'
    const timeToUse = time || '00:00';

    const formattedDate = formatDateTime(date, DATE_SUBMIT_YMD);
    const [hours, minutes] = timeToUse.split(':');

    const dateTimeString = `${formattedDate}T${hours}:${minutes}`;

    return formatDateTime(dateTimeString, dateFormat);
};

export const formatSpecificDateTimeISOJST = (date: Date | string, time: string | null) => {
    let m = moment(date).startOf('day');

    // Nếu có time, giả sử định dạng "HH:mm" và thiết lập giờ, phút, giây, mili giây
    if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        m = m.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
    }
    // Cộng 9 giờ để chuyển sang giờ Nhật (JST)
    // m = m.add(9, 'hours');

    // Sử dụng format của moment với định dạng ISO có timezone offset
    return m.format(DATE_TIME_SUBMIT_ISO_JST);
};

export const formatDateTimeISOJST = (datetime: Date | string, addJST: boolean = true) => {
    let m = moment.utc(datetime);
    if (addJST) {
        m = m.add(9, 'hours');
    }
    return m.format(DATE_TIME_SUBMIT_ISO_JST);
};

// #endregion List years, month combobox
export const yearsCombobox = (): number[] => {
    const startYear = 2025;
    const currentYear = (new Date()).getFullYear();

    const years: number[] = [];
    for (let year = startYear; year <= currentYear; year++) {
        years.push(year);
    }

    return [...years];
};

export const yearsListCombobox = (): number[] => {
    const startYear = 2025;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    const years: number[] = [];
    for (let year = startYear; year <= currentYear; year++) {
        years.push(year);
    }

    // Check if 6 months from now is a new year
    const sixMonthsLater = new Date(currentDate.setMonth(currentDate.getMonth() + 6));
    const nextYear = sixMonthsLater.getFullYear();
    if (nextYear > currentYear) {
        years.push(nextYear);
    }

    return [...years];
};

export const monthsCombobox: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
// #endregion
